<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'HandoverProtocol',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    contractId: {
      type: String,
      required: false,
    },
    revisionId: {
      type: String,
      required: false,
    },
    handoverType: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formName = ref('handoverProtocol');
  const formId = ref(null);
  const labelAlign = ref('left');
  const handoverTypes = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    handoverTypes: handoverTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('read_contract_protocol', {
          asResource: true,
          parameters: { id: props.oid },
        })
      : $fn.value.fetch('get_contract_protocol_template', {
          asResource: true,
          parameters: {
            contract_id: props.contractId,
            revision_id: props.revisionId,
            handover_type: props.handoverType,
          },
        });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_handoverTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('HandoverType');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'handoverTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      handoverTypes: $$load_handoverTypes,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      labelAlign,
      handoverTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('Contract', 'Handover protocol'),
          $fn.value.pgettext(
            'HandoverType',
            item.value.handover_type,
            'Sentence'
          ),
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );
  watch(
    () => props.revisionId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('revisionId');
    }
  );
  watch(
    () => props.handoverType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('handoverType');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1s8zq9lft_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_closeModal_mnl9kevku_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_script_n184740br_value_callable = async (
    $event
  ) => {
    return item.value.reset($event.data);
  };
  const act_1040167445267876_script_n184740br_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_n184740br_value_callable($event);
      },
    };
  };
  const inst_evh_1040167445267876_success_z5wudjr7k_getWhen = async (
    $event
  ) => {
    return $event.data;
  };
  const act_1040167445267876_crud_4i3gge5d0_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_5534025912102772_emit_69f78222d_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_2140198529698299_closeComponent_usvhjxpg0_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_crud_rp6qmaalx_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: { ...item.value, protocol_status: 'cancelled' },
    };
  };
  const act_2140198529698299_crud_rp6qmaalx_getConfirm = async ($event) => {
    return {
      header: 'Jegyőkönyv érvénytelenítése',
      message: 'A jegyzőkönyv érvénytelenítés után már nem módosítható.',
      btnOk: 'Érvénytelenítés',
      severity: 'warning',
    };
  };
  const act_2434514588044777_closeComponent_cdmm1qt6m_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2434514588044777_crud_vmruwerjt_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: { ...item.value, protocol_status: 'completed' },
    };
  };
  const act_2434514588044777_crud_vmruwerjt_getConfirm = async ($event) => {
    return {
      header: 'Jegyőkönyv véglegesítése',
      message: 'A jegyzőkönyv véglegesítés után már nem módosítható.',
      btnOk: 'Véglegesítés',
      severity: 'warning',
    };
  };
  const act_2167826738365259_script_hkdrp0cbt_value_callable = async (
    $event
  ) => {
    return item.value.reset($event.data);
  };
  const act_2167826738365259_script_hkdrp0cbt_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2167826738365259_script_hkdrp0cbt_value_callable($event);
      },
    };
  };
  const inst_evh_2167826738365259_success_mnmvy5st3_getWhen = async (
    $event
  ) => {
    return $event.data;
  };
  const act_2167826738365259_crud_915hz7jz9_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_7253299514750264_closeComponent_4xwqiem4e_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };

  const act_7315092382398562_closeModal_1s8zq9lft =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1s8zq9lft_getActionArgs,
    });
  const inst_evh_7315092382398562_close_kcoted5pm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1s8zq9lft,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_kcoted5pm],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qamg8ro3r =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3ku7mrd3t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qamg8ro3r,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3ku7mrd3t],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_0a4pllw03 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_ax8adqv97 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_0a4pllw03,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ax8adqv97],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_6frn2ibpe =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_dd03pibek =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_6frn2ibpe,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_dd03pibek],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_mnl9kevku =
    new actionTypes.CloseModalAction({
      actionArgs: act_6466705138206980_closeModal_mnl9kevku_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_ghyj7o4nk =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_mnl9kevku,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_deleted_ghyj7o4nk],
    event: 'deleted',
  });
  const act_1040167445267876_script_n184740br = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_n184740br_getActionArgs,
  });
  const inst_evh_1040167445267876_success_z5wudjr7k =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_n184740br,
      event: 'success',
      when: inst_evh_1040167445267876_success_z5wudjr7k_getWhen,
      displayName: 'script',
    });
  const act_1040167445267876_crud_4i3gge5d0 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_4i3gge5d0_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_z5wudjr7k],
  });
  const inst_evh_1040167445267876_submit_2k1iamnet =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_4i3gge5d0,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_2k1iamnet],
    event: 'submit',
  });
  const act_5534025912102772_reloadComponentData_3drecpor6 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_djlyzyiu1 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_3drecpor6,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_djlyzyiu1],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_ricb3njr5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_oouy8n83u =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_ricb3njr5,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_oouy8n83u],
    event: 'close',
  });
  const act_5534025912102772_emit_69f78222d = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_69f78222d_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_h468htcoi =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_69f78222d,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_h468htcoi],
    event: 'changed',
  });
  const act_2140198529698299_closeComponent_usvhjxpg0 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_usvhjxpg0_getActionArgs,
    });
  const inst_evh_2140198529698299_success_h2e2qo1v2 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_usvhjxpg0,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2140198529698299_crud_rp6qmaalx = new actionTypes.CRUDAction({
    actionArgs: act_2140198529698299_crud_rp6qmaalx_getActionArgs,
    confirm: act_2140198529698299_crud_rp6qmaalx_getConfirm,
    displayName: 'cancel',
    events: [inst_evh_2140198529698299_success_h2e2qo1v2],
  });
  const inst_evh_2140198529698299_clickPreventStop_x02fdqjoj =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_crud_rp6qmaalx,
      event: 'click.prevent.stop',
      displayName: 'cancel',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_x02fdqjoj],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeComponent_cdmm1qt6m =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_cdmm1qt6m_getActionArgs,
    });
  const inst_evh_2434514588044777_success_yoopz9sy8 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_cdmm1qt6m,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2434514588044777_crud_vmruwerjt = new actionTypes.CRUDAction({
    actionArgs: act_2434514588044777_crud_vmruwerjt_getActionArgs,
    confirm: act_2434514588044777_crud_vmruwerjt_getConfirm,
    displayName: 'complete',
    events: [inst_evh_2434514588044777_success_yoopz9sy8],
  });
  const inst_evh_2434514588044777_clickPreventStop_nk5bd6gj9 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_crud_vmruwerjt,
      event: 'click.prevent.stop',
      displayName: 'complete',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_nk5bd6gj9],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_script_hkdrp0cbt = new actionTypes.ScriptAction({
    actionArgs: act_2167826738365259_script_hkdrp0cbt_getActionArgs,
  });
  const inst_evh_2167826738365259_success_mnmvy5st3 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_script_hkdrp0cbt,
      event: 'success',
      when: inst_evh_2167826738365259_success_mnmvy5st3_getWhen,
      displayName: 'script',
    });
  const act_2167826738365259_crud_915hz7jz9 = new actionTypes.CRUDAction({
    actionArgs: act_2167826738365259_crud_915hz7jz9_getActionArgs,
    displayName: 'save',
    events: [inst_evh_2167826738365259_success_mnmvy5st3],
  });
  const inst_evh_2167826738365259_clickPreventStop_fmvo4mjb0 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_crud_915hz7jz9,
      event: 'click.prevent.stop',
      displayName: 'save',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_fmvo4mjb0],
      event: 'click.prevent.stop',
    });
  const act_7253299514750264_closeComponent_4xwqiem4e =
    new actionTypes.CloseComponentAction({
      actionArgs: act_7253299514750264_closeComponent_4xwqiem4e_getActionArgs,
    });
  const inst_evh_7253299514750264_clickPreventStop_3dwpjv7w5 =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_closeComponent_4xwqiem4e,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_7253299514750264_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7253299514750264_clickPreventStop_3dwpjv7w5],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_kcoted5pm,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_3ku7mrd3t,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ax8adqv97,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_dd03pibek,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_ghyj7o4nk,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_z5wudjr7k,
    inst_evh_1040167445267876_submit_2k1iamnet,
    save: inst_evh_1040167445267876_submit_2k1iamnet,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_reload_djlyzyiu1,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_oouy8n83u,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_h468htcoi,
    inst_evh_5534025912102772_changed,
    inst_evh_2140198529698299_success_h2e2qo1v2,
    inst_evh_2140198529698299_clickPreventStop_x02fdqjoj,
    cancel: inst_evh_2140198529698299_clickPreventStop_x02fdqjoj,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_success_yoopz9sy8,
    inst_evh_2434514588044777_clickPreventStop_nk5bd6gj9,
    complete: inst_evh_2434514588044777_clickPreventStop_nk5bd6gj9,
    inst_evh_2434514588044777_clickPreventStop,
    inst_evh_2167826738365259_success_mnmvy5st3,
    inst_evh_2167826738365259_clickPreventStop_fmvo4mjb0,
    inst_evh_2167826738365259_clickPreventStop,
    inst_evh_7253299514750264_clickPreventStop_3dwpjv7w5,
    inst_evh_7253299514750264_clickPreventStop,
  };

  const evh_7315092382398562_close_kcoted5pm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_kcoted5pm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_3ku7mrd3t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3ku7mrd3t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ax8adqv97 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ax8adqv97.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_dd03pibek = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_dd03pibek.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ghyj7o4nk = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ghyj7o4nk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_z5wudjr7k = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_z5wudjr7k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_2k1iamnet = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_2k1iamnet.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_djlyzyiu1 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_djlyzyiu1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_oouy8n83u = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_oouy8n83u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_h468htcoi = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_h468htcoi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_h2e2qo1v2 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_h2e2qo1v2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_x02fdqjoj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_x02fdqjoj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_yoopz9sy8 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_yoopz9sy8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_nk5bd6gj9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_nk5bd6gj9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_success_mnmvy5st3 = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_success_mnmvy5st3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_fmvo4mjb0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_fmvo4mjb0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_3dwpjv7w5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_3dwpjv7w5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="HandoverProtocol"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcContractProtocolHeader
          :protocol="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcContractProtocolHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetHandoverProtocol
          :item="item"
          :form="forms[formName]"
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetHandoverProtocol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          variant="warning"
          icon="fa-times"
          :text="pgettext('HandoverProtocol', 'Cancel protocol')"
          :spinning="actions.cancel.isRunning"
          v-if="loaded && !item.isNew && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="formId"
          variant="success"
          icon="fa-save"
          :text="gettext('Complete')"
          :spinning="actions.complete.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          class="ms-auto"
          v-if="loaded && !item.isNew && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :text="gettext('Save')"
          :spinning="actions.save.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="loaded && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          variant="default"
          :text="gettext('Close')"
          :disabled="actions.save.isRunning"
          class="ms-3"
          @click.prevent.stop="
            evh_7253299514750264_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
